import React from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Stack,
  Typography,
} from '@mui/material';
import { Add, Refresh, Search, Circle } from '@mui/icons-material';
import {
  DispositivoRedeTipo,
  DISPOSITIVO_REDE_TIPOS,
} from '../../../services/dispositivoRedeService';
import { UseDispositivosRedeReturn } from './useDispositivosRede';
import { STATUS_COLORS, getIpExibicao, getTipoLabel } from './monitoramentoRedeUtils';

type DispositivosTabHeaderProps = Pick<
  UseDispositivosRedeReturn,
  | 'dispositivos'
  | 'loading'
  | 'search'
  | 'setSearch'
  | 'filtroTipo'
  | 'setFiltroTipo'
  | 'loadDispositivos'
  | 'handleOpenCreate'
>;

const DispositivosTabHeader: React.FC<DispositivosTabHeaderProps> = ({
  dispositivos,
  loading,
  search,
  setSearch,
  filtroTipo,
  setFiltroTipo,
  loadDispositivos,
  handleOpenCreate,
}) => (
  <Box
    display="flex"
    gap={0.5}
    alignItems="center"
    sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 520 }, flex: { xs: 1, md: 'unset' }, flexWrap: 'wrap' }}
  >
    <Autocomplete
      size="small"
      options={dispositivos}
      getOptionLabel={(option) => option.nome}
      inputValue={search}
      onInputChange={(_, value) => setSearch(value)}
      filterOptions={(options, { inputValue }) => {
        const query = inputValue.trim().toLowerCase();
        return options.filter((option) =>
          option.nome.toLowerCase().includes(query) ||
          (option.endereco_ip || '').toLowerCase().includes(query) ||
          getIpExibicao(option).toLowerCase().includes(query)
        );
      }}
      noOptionsText="Nenhum dispositivo encontrado"
      sx={{ flex: 1, minWidth: 180 }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Pesquisar dispositivo..."
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Search sx={{ ml: 0.5, mr: 0.5, color: 'action.active', fontSize: 18 }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
          sx={{
            bgcolor: 'background.paper',
            '& .MuiOutlinedInput-root': { height: 32 },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.25 }}>
            <Circle
              sx={{
                fontSize: 10,
                color: STATUS_COLORS[option.monitor_status as keyof typeof STATUS_COLORS] ?? STATUS_COLORS.unknown,
              }}
            />
            <Box>
              <Typography variant="body2" fontWeight={600}>{option.nome}</Typography>
              <Typography variant="caption" color="text.secondary">
                {getTipoLabel(option.tipo)} · {getIpExibicao(option)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}
    />
    <FormControl
      size="small"
      sx={{ minWidth: 130, bgcolor: 'background.paper', '& .MuiOutlinedInput-root': { height: 32 } }}
    >
      <InputLabel sx={{ fontSize: '0.75rem' }}>Tipo</InputLabel>
      <Select
        label="Tipo"
        value={filtroTipo}
        onChange={(e) => setFiltroTipo(e.target.value as DispositivoRedeTipo | '')}
        sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
      >
        <MenuItem value="">Todos</MenuItem>
        {DISPOSITIVO_REDE_TIPOS.map((t) => (
          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
    <Button
      variant="outlined"
      size="small"
      onClick={() => void loadDispositivos()}
      startIcon={<Refresh sx={{ fontSize: 18 }} />}
      disabled={loading}
      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      Atualizar
    </Button>
    <Button
      variant="contained"
      size="small"
      onClick={handleOpenCreate}
      startIcon={<Add sx={{ fontSize: 18 }} />}
      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      Novo dispositivo
    </Button>
  </Box>
);

export default DispositivosTabHeader;
